import {
    trackEvent,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
} from 'dibs-tracking';
import classnames from 'classnames';
import { useFragment, graphql } from 'react-relay';
import { type FC, useContext, useEffect } from 'react';

// components
import { PriceDisplay } from 'dibs-buyer-price';
import { FormattedMessage } from 'dibs-react-intl';
import { PdpSharedContactSellerButton } from '../../shared/PdpSharedContactSellerButton';
import { PdpSharedPurchaseButton } from '../../PdpSharedButtons/PdpSharedPurchaseButton';
import { PdpRespMarketplaceStickyHeaderContext } from './PdpRespMarketplaceStickyHeaderContext';
import { PdpSharedBuyItNowButton } from '../../shared/PdpSharedShoppingButtons/PdpSharedBuyItNowButton';

// types
import { type PdpRespMarketplaceStickyHeader_sku$key } from './__generated__/PdpRespMarketplaceStickyHeader_sku.graphql';
import { type PdpRespMarketplaceStickyHeader_item$key } from './__generated__/PdpRespMarketplaceStickyHeader_item.graphql';
import { type PdpRespMarketplaceStickyHeader_user$key } from './__generated__/PdpRespMarketplaceStickyHeader_user.graphql';
import { type PdpRespMarketplaceStickyHeader_viewer$key } from './__generated__/PdpRespMarketplaceStickyHeader_viewer.graphql';
import { type PdpRespMarketplaceStickyHeader_displayInfo$key } from './__generated__/PdpRespMarketplaceStickyHeader_displayInfo.graphql';

// styles
import dibsCss from 'dibs-css';
import styles from './PdpRespMarketplaceStickyHeader.scss';

const viewerFragment = graphql`
    fragment PdpRespMarketplaceStickyHeader_viewer on Viewer {
        isMultiItemCart: featureFlag(feature: "multiItemCart")
    }
`;

const itemFragment = graphql`
    fragment PdpRespMarketplaceStickyHeader_item on Item {
        serviceId
        title
        isOnHold
        isPriceUponRequest
        ...PriceDisplay_item
            @arguments(
                userId: $userId
                isTrade: $isTrade
                userCountryCode: $userCountryCode
                priceBookName: $priceBookName
            )
        ...PdpSharedContactSellerButton_item
        ...PdpSharedPurchaseButton_item
        ...PdpSharedBuyItNowButton_item
    }
`;

const skuFragment = graphql`
    fragment PdpRespMarketplaceStickyHeader_sku on ItemSkuType {
        ...PriceDisplay_sku
            @include(if: $isMultiSku)
            @arguments(
                hasSkuId: true
                userId: $userId
                isTrade: $isTrade
                userCountryCode: $userCountryCode
                priceBookName: $priceBookName
            )
    }
`;

const userFragment = graphql`
    fragment PdpRespMarketplaceStickyHeader_user on User {
        serviceId
        ...PdpSharedContactSellerButton_user
    }
`;

const displayInfoFragment = graphql`
    fragment PdpRespMarketplaceStickyHeader_displayInfo on ItemDisplayInfo {
        ctas {
            showAcceptOffer
        }
    }
`;

export const PdpRespMarketplaceStickyHeader: FC<{
    viewer: PdpRespMarketplaceStickyHeader_viewer$key;
    item: PdpRespMarketplaceStickyHeader_item$key;
    skuId?: string;
    sku?: PdpRespMarketplaceStickyHeader_sku$key | null;
    user?: PdpRespMarketplaceStickyHeader_user$key | null;
    displayInfo?: PdpRespMarketplaceStickyHeader_displayInfo$key | null;
    currency: string;
    itemQuantity?: number;
}> = ({
    viewer: viewerRef,
    item: itemRef,
    skuId,
    sku: skuRef,
    user: userRef,
    displayInfo: displayInfoRef,
    currency,
    itemQuantity,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const sku = useFragment(skuFragment, skuRef);
    const user = useFragment(userFragment, userRef);
    const displayInfo = useFragment(displayInfoFragment, displayInfoRef);

    const userId = user?.serviceId;

    const { serviceId: itemId, title, isPriceUponRequest, isOnHold } = item;

    const showAcceptOffer = displayInfo?.ctas?.showAcceptOffer;
    const showInquire = isPriceUponRequest;
    const showBuy = !isPriceUponRequest && !isOnHold;

    const { showStickyHeader } = useContext(PdpRespMarketplaceStickyHeaderContext);

    useEffect(() => {
        if (showStickyHeader) {
            trackEvent({
                eventName: eventNameConstants.EVENT_PRODUCT_INTERACTION,
                interaction_type: interactionTypeConstants.INTERACTION_TYPE_PDP,
                step_interaction_name:
                    stepInteractionConstants.STEP_INTERACTION_PDP_STICKY_HEADER_DISPLAY,
            });
        }
    }, [showStickyHeader]);

    if (!title || !itemId) {
        return null;
    }

    return (
        <div
            className={classnames(
                dibsCss.flex,
                dibsCss.top0,
                dibsCss.fixed,
                dibsCss.zHigh,
                styles.header,
                dibsCss.bgWhite,
                dibsCss.shrink0,
                dibsCss.gapSmall,
                dibsCss.pyXsmall,
                dibsCss.pxLarger,
                dibsCss.itemsStart,
                dibsCss.itemsCenter,
                dibsCss.justifyBetween,
                dibsCss.overflowHidden,
                {
                    [styles.isOpen]: showStickyHeader,
                    [dibsCss.opacity100]: showStickyHeader,
                    [dibsCss.opacity0]: !showStickyHeader,
                }
            )}
        >
            <div className={classnames(dibsCss.flex, dibsCss.overflowHidden)}>
                <div
                    className={classnames(
                        showAcceptOffer ? styles.titleMultiline : styles.title,
                        dibsCss.sassyFontBodySmallHeavy,
                        dibsCss.textLeft
                    )}
                >
                    {title}
                </div>
            </div>

            <div className={classnames(dibsCss.flex, dibsCss.gapMedium, dibsCss.itemsCenter)}>
                {!showAcceptOffer && (
                    <PriceDisplay
                        showCompactedPrice
                        useSvgIcon
                        textSize="small"
                        currency={currency}
                        item={item}
                        sku={sku}
                    />
                )}
                <div>
                    {(() => {
                        if (showInquire) {
                            return (
                                <PdpSharedContactSellerButton
                                    user={user}
                                    item={item}
                                    type="primary"
                                    size="medium"
                                    launchOrigin="sticky header"
                                    dataTn="request-more-info"
                                    currency={currency}
                                    itemQuantity={itemQuantity}
                                    modalType="REQUEST_PRICE"
                                    className={dibsCss.minW200px}
                                >
                                    <FormattedMessage
                                        id="PdpRespMarketplaceStickyHeader.inquire"
                                        defaultMessage="Inquire"
                                    />
                                </PdpSharedContactSellerButton>
                            );
                        } else if (showBuy) {
                            return viewer.isMultiItemCart ? (
                                <PdpSharedBuyItNowButton
                                    size="medium"
                                    item={item}
                                    skuId={skuId}
                                    quantitySelected={itemQuantity?.toString()}
                                    location="STICKY_HEADER"
                                    className={dibsCss.minW200px}
                                />
                            ) : (
                                <PdpSharedPurchaseButton
                                    size="medium"
                                    item={item}
                                    itemId={itemId}
                                    userId={userId}
                                    quantitySelected={itemQuantity?.toString()}
                                    skuId={skuId}
                                    hasPrivateOffer={showAcceptOffer}
                                    location="STICKY_HEADER"
                                    className={classnames(dibsCss.block, dibsCss.minW200px)}
                                />
                            );
                        }
                        return null;
                    })()}
                </div>
            </div>
        </div>
    );
};
